/**
 * JWT 토큰 검증 유틸리티
 * 토큰 유효성 검사 및 만료 시간 관리
 */

import { formatDateTimeKST } from './dateUtils';

export interface JWTPayload {
    sub?: string;
    iat?: number;
    exp?: number;
    [key: string]: any;
}

export interface TokenValidationResult {
    isValid: boolean;
    isExpired: boolean;
    isExpiringSoon: boolean;
    payload?: JWTPayload;
    error?: string;
    expiresIn?: number; // 초 단위
}

/**
 * JWT 토큰 파싱 및 검증
 */
export class JWTValidator {
    /**
     * JWT 토큰에서 페이로드 추출
     */
    static parseToken(token: string): JWTPayload | null {
        try {
            if (!token || typeof token !== 'string') {
                return null;
            }

            const parts = token.split('.');
            if (parts.length !== 3) {
                return null;
            }

            const payload = parts[1];
            const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
            return JSON.parse(decoded);
        } catch (error) {
            console.error('JWT 토큰 파싱 오류:', error);
            return null;
        }
    }

    /**
     * 토큰 유효성 검사
     */
    static validateToken(token: string): TokenValidationResult {
        const result: TokenValidationResult = {
            isValid: false,
            isExpired: false,
            isExpiringSoon: false,
        };

        try {
            // 토큰 존재 여부 확인
            if (!token || typeof token !== 'string') {
                result.error = '토큰이 없거나 형식이 올바르지 않습니다.';
                return result;
            }

            // 토큰 형식 확인 (Bearer 제거)
            const cleanToken = token.replace(/^Bearer\s+/i, '');

            // JWT 형식 확인 (3개 부분으로 구성)
            const parts = cleanToken.split('.');
            if (parts.length !== 3) {
                result.error = 'JWT 토큰 형식이 올바르지 않습니다.';
                return result;
            }

            // 페이로드 파싱
            const payload = this.parseToken(cleanToken);
            if (!payload) {
                result.error = '토큰 페이로드를 파싱할 수 없습니다.';
                return result;
            }

            result.payload = payload;

            // 만료 시간 확인
            if (!payload.exp) {
                result.error = '토큰에 만료 시간이 없습니다.';
                return result;
            }

            const currentTime = Math.floor(Date.now() / 1000);
            const expirationTime = payload.exp;
            const expiresIn = expirationTime - currentTime;

            result.expiresIn = expiresIn;
            result.isExpired = expiresIn <= 0;
            result.isExpiringSoon = expiresIn > 0 && expiresIn < 30; // 30초 이내로 변경 (실제 만료 직전)
            result.isValid = !result.isExpired;

            return result;
        } catch (error) {
            result.error = `토큰 검증 중 오류가 발생했습니다: ${error}`;
            return result;
        }
    }

    /**
     * 토큰이 만료되었는지 확인
     */
    static isTokenExpired(token: string): boolean {
        const validation = this.validateToken(token);
        return validation.isExpired;
    }

    /**
     * 토큰이 곧 만료되는지 확인 (실제 만료 30초 전)
     */
    static isTokenExpiringSoon(token: string): boolean {
        const validation = this.validateToken(token);
        return validation.isExpiringSoon;
    }

    /**
     * 토큰 만료까지 남은 시간 (초)
     */
    static getTokenExpirationTime(token: string): number {
        const validation = this.validateToken(token);
        return validation.expiresIn || 0;
    }

    /**
     * 토큰 만료까지 남은 시간 (분)
     */
    static getTokenExpirationMinutes(token: string): number {
        const seconds = this.getTokenExpirationTime(token);
        return Math.floor(seconds / 60);
    }

    /**
     * 토큰 정보를 사람이 읽기 쉬운 형태로 반환
     */
    static getTokenInfo(token: string): {
        isValid: boolean;
        isExpired: boolean;
        isExpiringSoon: boolean;
        expiresIn: string;
        issuedAt?: string;
        subject?: string;
        error?: string;
    } {
        const validation = this.validateToken(token);

        if (!validation.isValid) {
            return {
                isValid: false,
                isExpired: validation.isExpired,
                isExpiringSoon: false,
                expiresIn: 'N/A',
                error: validation.error
            };
        }

        const payload = validation.payload!;

        return {
            isValid: true,
            isExpired: validation.isExpired,
            isExpiringSoon: validation.isExpiringSoon,
            expiresIn: this.formatExpirationTime(validation.expiresIn || 0),
            issuedAt: payload.iat
                ? formatDateTimeKST(new Date(payload.iat * 1000).toISOString())
                : undefined,
            subject: payload.sub
        };
    }

    /**
     * 만료 시간을 사람이 읽기 쉬운 형태로 포맷
     */
    private static formatExpirationTime(seconds: number): string {
        if (seconds <= 0) {
            return '만료됨';
        }

        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        if (days > 0) {
            return `${days}일 ${hours % 24}시간`;
        } else if (hours > 0) {
            return `${hours}시간 ${minutes % 60}분`;
        } else if (minutes > 0) {
            return `${minutes}분`;
        } else {
            return `${seconds}초`;
        }
    }

    /**
     * 토큰 갱신이 필요한지 확인 (실제 만료 시간 기반)
     */
    static shouldRefreshToken(token: string): boolean {
        const validation = this.validateToken(token);

        // 토큰이 유효하지 않거나 만료된 경우
        if (!validation.isValid || validation.isExpired) {
            return true;
        }

        // 토큰이 곧 만료되는 경우 (30초 이내) - 실제 만료 직전에만 갱신
        if (validation.isExpiringSoon) {
            return true;
        }

        return false;
    }

    /**
     * Access token 갱신이 필요한지 확인 (더 정교한 로직)
     * @param accessToken Access token
     * @param refreshToken Refresh token (선택적)
     * @returns 갱신 필요 여부
     */
    static shouldRefreshAccessToken(accessToken: string, refreshToken?: string | null): boolean {
        const accessValidation = this.validateToken(accessToken);

        // Access token이 유효하지 않거나 만료된 경우
        if (!accessValidation.isValid || accessValidation.isExpired) {
            return true;
        }

        // Access token이 곧 만료되는 경우 (30초 이내)
        if (accessValidation.isExpiringSoon) {
            // Refresh token이 제공된 경우, refresh token도 유효한지 확인
            if (refreshToken) {
                const refreshValidation = this.validateToken(refreshToken);
                // Refresh token이 유효한 경우에만 갱신
                return refreshValidation.isValid && !refreshValidation.isExpired;
            }
            // Refresh token이 없으면 갱신 불가
            return false;
        }

        return false;
    }

    /**
     * 토큰 갱신 권장 시간 (갱신이 필요한 시간)
     */
    static getRecommendedRefreshTime(token: string): number {
        const validation = this.validateToken(token);

        if (!validation.isValid || !validation.expiresIn) {
            return 0; // 즉시 갱신 필요
        }

        // 토큰 만료 30초 전에 갱신 권장
        return Math.max(0, validation.expiresIn - 30);
    }
}

export default JWTValidator;
